import { useEffect, useState } from 'react';
import { executeNoBodyRequest } from 'src/common/hooks/widgets/fetchData';
import { apiUrl } from '../constants/default';

/**
 * Get api data with respect to locale
 *
 * @param {string} localeCode the language code to get the currency sign for
 * @returns {Array}
 */

interface AssessmentTranslation {
  title: string;
  description: string;
  cta: string;
  abstract: string;
  buttons: Button[];
  sections: Section[];
  text_right: string | null;
  chartValues: string[];
}

interface Button {
  label: string;
  href: string;
  name: string;
}

interface Section {
  section: string;
  cclc: string;
  question: string;
  description: string;
  abstract: string;
  isbuttonEnabled: boolean;
  buttonLabel: string;
  answers: Answer[];
  subsection: Subsection[];
}

interface Answer {
  answer: string;
  weightage: number;
  desc: string;
}

interface Subsection {
  heading: string;
  desc: string;
  featureName: string;
  featureDesc: string;
  features: string[];
  questionName: string;
  questions: Question[];
}

interface Question {
  id: number;
  question: string;
  from: string;
  to: string;
  answers: AnswerOption[];
}

interface AnswerOption {
  answer: string;
  weightage: number;
}

export interface PDFData {
  page1: Page;
  page2: PageWithDesc;
  page3: ResultsPage;
  page4: ResultsPage;
  page5: OverallAssessmentPage;
}

interface Page {
  heading: string;
  image: string;
  logo: string;
}

interface PageWithDesc {
  heading: string;
  desc: string;
  link: string;
}

interface ResultsPage {
  heading: string;
  sections: ResultSection[];
}

interface ResultSection {
  heading: string;
  scoreLabel: string;
  color: string;
  desc: string;
}

interface OverallAssessmentPage {
  heading: string;
  color: string;
  scoreLabel: string;
  scoreKeyLabel: string;
  scoreLimit: ScoreLimit[];
  footnote: string;
  footNoteLink: string;
  footNoteBtnLabel: string;
}

interface ScoreLimit {
  label: string;
  desc: string;
}

interface Translation {
  assessment: AssessmentTranslation | null;
  pdfData: PDFData | null;
}

interface CyberResilienceAssessment {
  translation: Translation;
}

export const useData = (localeCode: string): Translation => {
  const [assessment, setAssessmentData] =
    useState<AssessmentTranslation | null>(null);
  const [pdfData, setPdfData] = useState<PDFData | null>(null);

  const fetchData = async () => {
    const hostingService = apiUrl(localeCode);
    await executeNoBodyRequest(`${hostingService}`, 'GET')
      .then((response) => {
        if (response.status === 200) {
          const content =
            response.message && response.message.length > 0
              ? response.message[0]
              : null;
          const vars = content as unknown as CyberResilienceAssessment;
          const variables = vars.translation as Translation;
          const assessment = variables.assessment as AssessmentTranslation;
          const pdfData = variables.pdfData as PDFData;
          setAssessmentData(assessment);
          setPdfData(pdfData);
        }
      })
      .catch((error) => {
        console.log('Error:', error);
      });
  };

  useEffect(() => {
    fetchData();
  }, [localeCode]);

  return { assessment, pdfData };
};
